import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Col, Form, Row, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../utils/apiRoutes";
import { commonService } from "../../utils/commonService";
import Loader from "../Loader/Loader";
import { errorToastMessage, successToastMessage } from "../../utils/toastMessage";
import supportTicket from "../../Assets/Images/Revenue-management/support-ticket.svg";


function getStatusVariant(status) {
    switch ((status || "").toLowerCase()) {
        case "open":
            return "info";
        case "in progress":
            return "primary";
        case "on hold":
            return "warning";
        case "resolved":
            return "success";
        case "closed":
            return "secondary";
        default:
            return "dark";
    }
}

function SupportTicketDetail({ ticketId: ticketIdProp, onBack, embedded = false } = {}) {
    const params = useParams();
    const ticketid = ticketIdProp || params.ticketid;
    const navigate = useNavigate();
    const [ticketData, setTicketData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [replyText, setReplyText] = useState("");
    const [replyFiles, setReplyFiles] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    const loadTicketDetails = async () => {
        setLoading(true);
        try {
            const response = await axios.post(
                API_URL.BASE_API_URL.ADMINAPI + API_URL.PUBLIC_SUPPORT_TICKET.GET_BY_PRIMARY_KEY,
                {
                    ticketid: Number(ticketid),
                    clientid: Number(commonService.getLoginUserData()?.clientid || 0),
                }
            );

            if (response?.data?.status_code === 1) {
                setTicketData(response?.data?.data || null);
            } else {
                errorToastMessage(response?.data?.message || "Unable to load ticket details.");
                setTicketData(null);
            }
        } catch (error) {
            errorToastMessage(error?.response?.data?.message || error?.message || "Unable to load ticket details.");
            setTicketData(null);
        }
        setLoading(false);
    };

    useEffect(() => {
        if (ticketid) {
            loadTicketDetails();
        }
    }, [ticketid]);

    const attachmentsByMessageId = useMemo(() => {
        const grouped = {};
        (ticketData?.attachments || []).forEach((item) => {
            const key = item?.ticketmessageid || 0;
            if (!grouped[key]) {
                grouped[key] = [];
            }
            grouped[key].push(item);
        });
        return grouped;
    }, [ticketData]);

    const getAttachmentTypeMeta = (fileName: string): AttachmentTypeMeta => {
        const ext = (fileName.split(".").pop() || "").toLowerCase();

        if (ext === "pdf") {
            return { label: "PDF", bg: "#ffe8e8", color: "#c62828" };
        }

        if (["doc", "docx", "rtf", "txt"].includes(ext)) {
            return { label: "DOC", bg: "#e8f0ff", color: "#1e4fbf" };
        }

        if (["xls", "xlsx", "csv"].includes(ext)) {
            return { label: "XLS", bg: "#e8f7ee", color: "#1b7f46" };
        }

        if (["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"].includes(ext)) {
            return { label: "IMG", bg: "#fff6e6", color: "#b26a00" };
        }

        if (["zip", "rar", "7z", "tar", "gz"].includes(ext)) {
            return { label: "ZIP", bg: "#f1ecff", color: "#5e35b1" };
        }

        return { label: "FILE", bg: "#edf1f7", color: "#3d4a5c" };
    };

    const handleReplySubmit = async (event) => {
        event.preventDefault();

        if (!replyText.trim()) {
            errorToastMessage("Please enter a reply message.");
            return;
        }

        setSubmitting(true);
        try {
            const payload = new FormData();
            payload.append("ticketid", ticketid);
            payload.append("clientid", Number(commonService.getLoginUserData()?.clientid || 0));
            payload.append("userid", Number(commonService.getLoginUserData()?.userid || 0));
            payload.append("detail", replyText.trim());
            replyFiles.forEach((file) => payload.append("files", file));

            const response = await axios({
                method: "POST",
                url: API_URL.BASE_API_URL.ADMINAPI + API_URL.PUBLIC_SUPPORT_TICKET.REPLY,
                data: payload,
            });

            if (response?.data?.status_code === 1) {
                successToastMessage(response?.data?.message);
                setReplyText("");
                setReplyFiles([]);
                loadTicketDetails();
            } else {
                errorToastMessage(response?.data?.message || "Unable to submit reply.");
            }
        } catch (error) {
            errorToastMessage(error?.response?.data?.message || error?.message || "Unable to submit reply.");
        } finally {
            setSubmitting(false);
        }
    };

    const ticket = ticketData?.ticket || null;
    const messages = ticketData?.messages || [];

    return (
        <div className={embedded ? "" : "content-wrapper"}>
            {loading ? (
                <Loader />
            ) : ticket ? (
                <div className="support-ticket-detail-shell">
                    <div class="ticket-container">

                        <div class="ticket-header">
                            <div class="ticket-header-left">
                                <div class="ticket-icon">
                                    <img src={supportTicket} alt="" />
                                </div>

                                <div class="ticket-title-area">
                                    <div class="ticket-title">{ticket?.title}</div>
                                    <div class="ticket-number">{ticket?.ticketno}</div>

                                    <div class="ticket-meta">
                                        <div class="ticket-meta-item">▦&nbsp; Category : <strong>{ticket?.category_name || "-"}</strong></div>
                                        <div class="ticket-meta-item">♙&nbsp; Client : <strong>{ticket?.createdby_name || "-"}</strong></div>
                                        <div class="ticket-meta-item">♙&nbsp; Created By : <strong>{ticket?.createdby_name || "-"}</strong></div>
                                        <div class="ticket-meta-item">↔&nbsp; Close On : <strong>{commonService.getConvertDateInTimeZone(ticket?.closedon, null, "MM/DD/yyyy HH:mm")}</strong></div>
                                        <div class="ticket-meta-item">▣&nbsp; Created on : <strong>{commonService.getConvertDateInTimeZone(ticket?.createdon, null, "MM/DD/yyyy HH:mm")}</strong></div>
                                    </div>
                                </div>
                            </div>

                            <div class="ticket-actions">
                                <button class={`btn-status btn bg-${getStatusVariant(ticket?.status)}`}>
                                    {ticket?.status || "-"}
                                </button>
                            </div>
                        </div>

                        <div class="conversation-title">Conversation</div>

                        {messages?.length > 0 ? (
                            <div className="d-grid gap-3">
                                {messages.map((item) => {
                                    const messageAttachments =
                                        attachmentsByMessageId?.[item?.ticketmessageid] || [];

                                    const senderName = item?.sender_associationtype === "Client" ? item?.sender_name || "-" : "Support";

                                    return (
                                        <div
                                            key={item?.ticketmessageid} className={`message ${item?.sender_associationtype === "Client" ? "message-primary" : "message-secondary"}`}>
                                            <div className="message-content">


                                                <div className="avatar" style={{ background: commonService.CreateColor(senderName).BGColor, color: commonService.CreateColor(senderName).FontColor }}>
                                                    {senderName !== "-"
                                                        ? senderName.charAt(0).toUpperCase()
                                                        : "?"}
                                                </div>

                                                <div className="message-body">

                                                    {/* Header */}
                                                    <div className="message-header">
                                                        <div className="message-user">
                                                            {senderName}
                                                        </div>

                                                        <div className="message-date">
                                                            ▣&nbsp;
                                                            <b>{commonService.getConvertDateInTimeZone(
                                                                item?.createdon,
                                                                null,
                                                                "MM/DD/yyyy HH:mm"
                                                            )}</b>
                                                        </div>
                                                    </div>


                                                    <div className="message-text" dangerouslySetInnerHTML={{ __html: item?.detail }}>

                                                    </div>

                                                    {messageAttachments?.length > 0 && (
                                                        <div className="attachments">
                                                            {messageAttachments.map((a) => {
                                                                const typeMeta =
                                                                    getAttachmentTypeMeta(
                                                                        a?.file_name || ""
                                                                    );

                                                                return (
                                                                    <a
                                                                        key={a?.ticketattachmentid}
                                                                        href={a?.file_path}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                        className={`attachment ${typeMeta.className || ""}`}
                                                                        title={a?.file_name}
                                                                        aria-label={a?.file_name}
                                                                        style={{
                                                                            background: typeMeta.bg,
                                                                            color: typeMeta.color,
                                                                        }}
                                                                    >
                                                                        <span>
                                                                            {typeMeta.label}
                                                                        </span>
                                                                    </a>
                                                                );
                                                            })}
                                                        </div>
                                                    )}

                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="support-ticket-empty-state">
                                No messages available.
                            </div>
                        )}




                        <div class="reply-title">Reply</div>

                        <textarea class="reply-textarea" placeholder="Write Here..." rows={5} value={replyText} onChange={(event) => setReplyText(event.target.value)}></textarea>

                        <div class="file-upload">
                            <label class="file-label">
                                    <span>⌕</span> {replyFiles?.length > 0 ? replyFiles.map((file) => file.name).join(" , ") : "Choose File"}
                                <input class="file-input" type="file" multiple onChange={(event) => setReplyFiles(Array.from(event.target.files || []))} />
                            </label>
                            {/*<span class="file-name">{replyFiles?.length > 0 ? replyFiles.map((file) => file.name).join(" , ") : "No File Chosen"}</span>*/}
                        </div>

                        <div class="send-container">
                            {submitting ?
                                <button class="btn-send">
                                    <Spinner animation="border" size="sm" className="me-2" /> Send Reply
                                </button> :
                                <button class="btn-send" onClick={(e) => handleReplySubmit(e)}>
                                    <span>➤</span>&nbsp; Send Reply
                                </button>
                            }
                        </div>
                    </div>
                </div>
            ) : (
                <Card className="shadow-sm border-0">
                    <Card.Body className="text-center py-5">
                        <div className="text-muted mb-3">Ticket details were not found or you do not have access.</div>
                        <Button
                            variant="outline-primary"
                            onClick={() => {
                                if (onBack) {
                                    onBack();
                                    return;
                                }

                                navigate("/support-ticket");
                            }}
                        >
                            Back to list
                        </Button>
                    </Card.Body>
                </Card>
            )}
        </div>
    );
}

export default SupportTicketDetail;